import React, { useState, useMemo, useEffect } from 'react';
import { ActSummary, ChapterSummary } from '../../types/tome';
import { Search, X, ChevronDown, ChevronRight, BookOpen } from 'lucide-react';

export interface TableOfContentsProps {
  isOpen: boolean;
  currentPage: number;
  onClose: () => void;
  onJumpToPage: (pageNumber: number) => void;
  acts: ActSummary[];
  chapters: ChapterSummary[];
}

export const TableOfContents: React.FC<TableOfContentsProps> = ({
  isOpen,
  currentPage,
  onClose,
  onJumpToPage,
  acts,
  chapters,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedActs, setExpandedActs] = useState<Record<number, boolean>>({});

  // Auto-expand active act when opened
  useEffect(() => {
    if (isOpen) {
      const currentAct = acts.find(
        (a) => currentPage >= a.startPage && currentPage <= a.endPage
      );
      if (currentAct) {
        setExpandedActs((prev) => ({ ...prev, [currentAct.actNumber]: true }));
      }
    }
  }, [isOpen, currentPage, acts]);

  // Escape key listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  const toggleAct = (actNum: number) => {
    setExpandedActs((prev) => ({ ...prev, [actNum]: !prev[actNum] }));
  };

  const filteredChapters = useMemo(() => {
    if (!searchQuery.trim()) return null;
    const q = searchQuery.toLowerCase();
    return chapters.filter(
      (c) =>
        c.title.toLowerCase().includes(q) ||
        `page ${c.startPage}`.includes(q) ||
        (c.contentArchetype && c.contentArchetype.toLowerCase().includes(q))
    );
  }, [chapters, searchQuery]);

  if (!isOpen) return null;

  return (
    <div className="drawer-overlay" onClick={onClose} role="presentation">
      <aside
        className="drawer-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Table of Contents"
      >
        <div className="drawer-header">
          <div className="drawer-title-group">
            <BookOpen size={18} className="drawer-icon-accent" />
            <span className="drawer-badge">NAV-INDEX</span>
            <h2 className="drawer-title">Table of Contents</h2>
          </div>
          <button className="btn-close" onClick={onClose} aria-label="Close Table of Contents">
            <X size={20} />
          </button>
        </div>

        {/* Live Search */}
        <div className="drawer-search">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            className="search-input"
            placeholder="Search chapters, logs, archives..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            autoFocus
          />
          {searchQuery && (
            <button
              className="search-clear-btn"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              <X size={14} />
            </button>
          )}
        </div>

        {/* Scrollable Content */}
        <div className="drawer-content scrollable">
          {filteredChapters !== null ? (
            <div className="search-results">
              <div className="search-count-label">
                {filteredChapters.length}{' '}
                {filteredChapters.length === 1 ? 'record found' : 'records found'}
              </div>
              {filteredChapters.length === 0 ? (
                <div className="drawer-empty-state">
                  <p>NO RECORDS FOUND IN THE LEDGER CORRELATION MATRIX.</p>
                </div>
              ) : (
                filteredChapters.map((chapter) => {
                  const isActive =
                    currentPage >= chapter.startPage && currentPage <= chapter.endPage;
                  return (
                    <button
                      key={`${chapter.actNumber}-${chapter.chapterNumber}-${chapter.startPage}`}
                      className={`toc-chapter-item ${isActive ? 'active' : ''}`}
                      onClick={() => {
                        onJumpToPage(chapter.startPage);
                        onClose();
                      }}
                    >
                      <span className="toc-page-pill">P. {chapter.startPage}</span>
                      <span className="toc-chapter-name">{chapter.title}</span>
                      {chapter.contentArchetype && (
                        <span className="toc-archetype-tag">
                          {chapter.contentArchetype.toUpperCase()}
                        </span>
                      )}
                    </button>
                  );
                })
              )}
            </div>
          ) : (
            <div className="acts-accordion-list">
              {acts.map((act) => {
                const isExpanded = !!expandedActs[act.actNumber];
                const isCurrentAct =
                  currentPage >= act.startPage && currentPage <= act.endPage;
                const actChapters = chapters.filter((c) => c.actNumber === act.actNumber);

                return (
                  <div
                    key={act.actNumber}
                    className={`act-group ${isCurrentAct ? 'act-current' : ''}`}
                  >
                    <button
                      className="act-header"
                      onClick={() => toggleAct(act.actNumber)}
                      aria-expanded={isExpanded}
                    >
                      <div className="act-header-left">
                        <span className="act-indicator">
                          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                        </span>
                        <span className="act-title">{act.title}</span>
                      </div>
                      <span className="act-range">
                        PP. {act.startPage}–{act.endPage}
                      </span>
                    </button>

                    {isExpanded && (
                      <div className="act-chapters-container">
                        {actChapters.map((chapter) => {
                          const isActive =
                            currentPage >= chapter.startPage &&
                            currentPage <= chapter.endPage;
                          return (
                            <button
                              key={`${act.actNumber}-${chapter.chapterNumber}-${chapter.startPage}`}
                              className={`toc-chapter-item ${isActive ? 'active' : ''}`}
                              onClick={() => {
                                onJumpToPage(chapter.startPage);
                                onClose();
                              }}
                            >
                              <div className="toc-chapter-meta">
                                <span className="toc-page-pill">P. {chapter.startPage}</span>
                                {chapter.chapterNumber > 0 && (
                                  <span className="toc-num-pill">
                                    CH {chapter.chapterNumber}
                                  </span>
                                )}
                              </div>
                              <span className="toc-chapter-name">{chapter.title}</span>
                              {isActive && <span className="toc-active-marker">● HERE</span>}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </aside>
    </div>
  );
};
